import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/plugins/api';

interface AuthConfig {
  base_url: string;
  client_id: string;
  scope: string;
  scope_claim: string;
  role_claims: string[];
  required_scopes: string[];
}

const TOKEN_STORAGE_KEY = 'admin_jwt';
const PKCE_VERIFIER_KEY = 'admin_pkce_verifier';
const REDIRECT_KEY = 'admin_post_login';

function base64UrlDecode(segment: string): string {
  segment = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding = 4 - (segment.length % 4 || 4);
  if (padding !== 4) {
    segment += '='.repeat(padding);
  }
  return atob(segment);
}

function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return null;
    }
    const json = base64UrlDecode(payload);
    return JSON.parse(json);
  } catch (error) {
    console.warn('[AdminAuth] Failed to decode token payload', error);
    return null;
  }
}

function isExpired(claims: Record<string, unknown> | null): boolean {
  if (!claims || typeof claims.exp !== 'number') {
    return false;
  }
  const now = Math.floor(Date.now() / 1000);
  return claims.exp < now;
}

function extractClaimValues(claims: Record<string, unknown> | null, claimName: string): string[] {
  if (!claims) return [];
  const raw = claims[claimName];
  if (!raw) return [];

  const collected: string[] = [];
  if (Array.isArray(raw)) {
    raw.forEach((entry) => {
      if (typeof entry === 'string') {
        collected.push(entry.trim());
      }
    });
  } else if (typeof raw === 'string') {
    collected.push(
      ...raw
        .split(/[\s,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    );
  }

  return Array.from(new Set(collected));
}

function generateRandomString(length: number): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  let text = '';
  values.forEach((value) => {
    text += possible[value % possible.length];
  });
  return text;
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = '';
  bytes.forEach((byte) => {
    str += String.fromCharCode(byte);
  });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const hashed = await sha256(verifier);
  return base64UrlEncode(hashed);
}

export const useAdminAuthStore = defineStore('admin-auth', () => {
  const token = ref<string | null>(null);
  const claims = ref<Record<string, unknown> | null>(null);
  const config = ref<AuthConfig | null>(null);
  const hydrated = ref(false);
  const timeRemaining = ref<string | null>(null);
  const secondsRemaining = ref<number | null>(null);
  /** The session ran out while the app was open (cleared on the next login). */
  const sessionExpired = ref(false);
  let timerInterval: ReturnType<typeof setInterval> | null = null;

  function updateTimeRemaining() {
    if (!claims.value || typeof claims.value.exp !== 'number') {
      timeRemaining.value = null;
      secondsRemaining.value = null;
      return;
    }
    const now = Math.floor(Date.now() / 1000);
    const diff = claims.value.exp - now;

    if (diff <= 0) {
      timeRemaining.value = null;
      secondsRemaining.value = 0;
      sessionExpired.value = true;
      clearSession();
      return;
    }

    secondsRemaining.value = diff;
    sessionExpired.value = false;

    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    timeRemaining.value = `${hours}h ${minutes}m ${seconds}s`;
  }

  function startTimer() {
    stopTimer();
    updateTimeRemaining();
    timerInterval = setInterval(updateTimeRemaining, 1000);
  }

  function stopTimer() {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }
  const loading = ref(false);
  const configLoading = ref(false);
  const initialized = ref(false);
  const error = ref<string | null>(null);
  const customPermissions = ref<string[]>([]);

  async function fetchCustomPermissions() {
    if (!token.value) return;
    try {
      const response = await api.get<{ email: string; permissions: string[] }>(
        '/api/permissions/me',
        {
          headers: { Authorization: `Bearer ${token.value}` },
        },
      );
      customPermissions.value = response.data.permissions;
    } catch (err) {
      console.error('[AdminAuth] Failed to fetch custom permissions', err);
    }
  }

  async function fetchConfig() {
    if (config.value || configLoading.value) return;
    configLoading.value = true;
    try {
      const response = await api.get<AuthConfig>('/api/config');
      config.value = response.data;
    } catch (err) {
      console.error('[AdminAuth] Failed to fetch auth config', err);
      error.value = 'Failed to load authentication configuration';
    } finally {
      configLoading.value = false;
    }
  }

  function hydrate() {
    if (hydrated.value) {
      return;
    }
    if (typeof window === 'undefined') {
      hydrated.value = true;
      return;
    }

    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const decoded = decodeToken(stored);
      if (!decoded || isExpired(decoded)) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      } else {
        token.value = stored;
        claims.value = decoded;
        // If we have a session, we MUST have the config to validate roles
        void fetchConfig();
        startTimer();
      }
    }

    hydrated.value = true;
    initialized.value = true;

    if (token.value) {
      void fetchCustomPermissions();
    }
  }

  function setToken(newToken: string) {
    token.value = newToken;
    claims.value = decodeToken(newToken);
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    void fetchCustomPermissions();
    startTimer();
  }

  function clearSession() {
    token.value = null;
    claims.value = null;
    secondsRemaining.value = null;
    customPermissions.value = [];
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    stopTimer();
  }

  function rememberRedirect(path: string) {
    sessionStorage.setItem(REDIRECT_KEY, path);
  }

  function consumeRedirect(): string | null {
    const stored = sessionStorage.getItem(REDIRECT_KEY);
    if (stored) {
      sessionStorage.removeItem(REDIRECT_KEY);
      return stored;
    }
    return null;
  }

  const roles = computed(() => {
    if (!config.value) return [];
    const baseScopes = extractClaimValues(claims.value, config.value.scope_claim);
    const extraScopes = config.value.role_claims.flatMap((claim) =>
      extractClaimValues(claims.value, claim),
    );
    return Array.from(new Set([...baseScopes, ...extraScopes]));
  });

  const hasRequiredRole = computed(() => {
    if (!config.value || !config.value.required_scopes.length) {
      return true;
    }
    return roles.value.some((scope) => config.value?.required_scopes.includes(scope));
  });

  const isAuthenticated = computed(() => Boolean(token.value));

  const displayName = computed(() => {
    if (!claims.value) return null;

    // First try to assemble from scoped claims
    const firstName = roles.value.find((s) => s.startsWith('firstName:'))?.split(':')[1];
    const lastName = roles.value.find((s) => s.startsWith('lastName:'))?.split(':')[1];

    if (firstName || lastName) {
      return [firstName, lastName].filter(Boolean).join(' ');
    }

    // Fallback to name or preferred_username
    return (claims.value.name as string) || (claims.value.preferred_username as string) || null;
  });

  const displayEmail = computed(() => (claims.value?.sub as string) || null);

  const userRoles = computed(() =>
    roles.value.filter((s) => s.startsWith('role:')).map((s) => s.replace('role:', '')),
  );

  const userPermissions = computed(() => {
    const fromToken = roles.value
      .filter((s) => s.startsWith('permission:'))
      .map((s) => s.replace('permission:', ''));
    return Array.from(new Set([...fromToken, ...customPermissions.value]));
  });

  const hasPermission = computed(() => (permission: string) => {
    return userPermissions.value.includes(permission);
  });

  const userTypes = computed(() =>
    roles.value.filter((s) => s.startsWith('type:')).map((s) => s.replace('type:', '')),
  );

  const doctorRoles = computed(() =>
    roles.value.filter((s) => s.startsWith('doctorRole:')).map((s) => s.replace('doctorRole:', '')),
  );

  function getAuthorizationHeader(): string | null {
    if (!token.value) {
      return null;
    }
    return `Bearer ${token.value}`;
  }

  async function startLogin(redirectUri: string, desiredPath: string) {
    if (!config.value) {
      await fetchConfig();
    }
    if (!config.value) {
      throw new Error('Apollo OAuth configuration missing');
    }
    const verifier = generateRandomString(128);
    const challenge = await generateCodeChallenge(verifier);

    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    rememberRedirect(desiredPath);

    const authUrl = new URL('/authorize', config.value.base_url);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('client_id', config.value.client_id);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('code_challenge', challenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('scope', config.value.scope);

    window.location.href = authUrl.toString();
  }

  async function finishLogin(code: string, redirectUri: string): Promise<string | null> {
    if (!config.value) {
      await fetchConfig();
    }
    if (!config.value) {
      throw new Error('Apollo OAuth configuration missing');
    }

    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!verifier) {
      throw new Error('Missing PKCE verifier. Please restart the login flow.');
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(`${config.value.base_url}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          client_id: config.value.client_id,
          code_verifier: verifier,
          redirect_uri: redirectUri,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.access_token) {
        const message = (data && (data.error_description || data.error)) || 'Token exchange failed';
        throw new Error(message);
      }

      setToken(data.access_token as string);
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);
      return consumeRedirect();
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      clearSession();
      throw err instanceof Error ? err : new Error(String(err));
    } finally {
      loading.value = false;
    }
  }

  function logout() {
    clearSession();
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
  }

  return {
    token,
    claims,
    timeRemaining,
    secondsRemaining,
    sessionExpired,
    roles,
    userRoles,
    userPermissions,
    userTypes,
    doctorRoles,
    displayName,
    displayEmail,
    loading,
    error,
    isAuthenticated,
    hasRequiredRole,
    hasPermission,
    customPermissions,
    hydrate,
    initialized,
    fetchConfig,
    fetchCustomPermissions,
    startLogin,
    finishLogin,
    logout,
    getAuthorizationHeader,
    rememberRedirect,
  };
});
